import {
  createEnrollmentClient,
  issueSessionBillingTicket,
  pledgeSessionPrompt,
  type SessionBillingScope,
} from '@mod/core/sharing'

/** RFC 0345: Google authenticates backup access; it never unlocks a backup. */
const PENDING = 'mod_sharing_sign_in'
const TOKEN = 'mod_sharing_account_token'
export interface SharingAccount {
  id: string
  email: string
  name: string
}
export interface SignInBrowser {
  location: Pick<Location, 'href' | 'assign'>
  history: Pick<History, 'replaceState'>
  sessionStorage: Storage
}
function authOrigin(value: string): string {
  const url = new URL(value)
  if (
    url.protocol !== 'https:' &&
    !(
      url.protocol === 'http:' &&
      ['localhost', '127.0.0.1'].includes(url.hostname)
    )
  )
    throw new Error('Sharing sign-in requires an HTTPS auth server')
  return url.origin
}
export function startSharingSignIn(
  authServer: string,
  browser: SignInBrowser = window,
) {
  const destination = new URL('/auth/sharing', authOrigin(authServer))
  const challenge = Array.from(
    crypto.getRandomValues(new Uint8Array(32)),
    (b) => b.toString(16).padStart(2, '0'),
  ).join('')
  browser.sessionStorage.setItem(
    PENDING,
    JSON.stringify({ challenge, expiresAt: Date.now() + 600_000 }),
  )
  destination.searchParams.set('challenge', challenge)
  browser.location.assign(destination.href)
}
/** Consume and scrub before mounting the app or opening any network connection. */
export function consumeSharingSignIn(browser: SignInBrowser = window): boolean {
  const url = new URL(browser.location.href)
  const params = new URLSearchParams(url.hash.slice(1))
  if (
    !params.has('sharing_challenge') &&
    !params.has('sharing_token') &&
    !params.has('sharing_error')
  )
    return false
  url.hash = ''
  browser.history.replaceState(null, '', url.href)
  const pending = browser.sessionStorage.getItem(PENDING)
  browser.sessionStorage.removeItem(PENDING)
  let saved: { challenge?: string; expiresAt?: number } = {}
  try {
    saved = JSON.parse(pending ?? '{}')
  } catch {
    /* reject below */
  }
  if (
    !saved ||
    typeof saved.challenge !== 'string' ||
    saved.challenge !== params.get('sharing_challenge') ||
    typeof saved.expiresAt !== 'number' ||
    saved.expiresAt < Date.now()
  )
    throw new Error('Sign-in did not start in this tab. Please sign in again.')
  if (params.has('sharing_error'))
    throw new Error(`Google sign-in failed: ${params.get('sharing_error')}`)
  const token = params.get('sharing_token')
  if (!token || token.length > 8192)
    throw new Error('Sign-in did not return a valid account token')
  browser.sessionStorage.setItem(TOKEN, token)
  return true
}
export function createSharingAccountClient(
  authServer: string,
  storage: Storage = sessionStorage,
  send: typeof fetch = fetch,
) {
  const origin = authOrigin(authServer)
  async function request(path: string, body?: unknown): Promise<unknown> {
    const token = storage.getItem(TOKEN)
    if (!token) throw new Error('Sign in with Google first')
    const response = await send(new URL(path, origin), {
      method: body === undefined ? 'GET' : 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: 'omit',
      redirect: 'error',
      cache: 'no-store',
    })
    if (response.status === 401) {
      storage.removeItem(TOKEN)
      throw new Error('Sign in with Google again')
    }
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as {
        error?: unknown
      } | null
      throw new Error(
        typeof body?.error === 'string'
          ? body.error
          : `Account request failed (${response.status})`,
      )
    }
    return response.json()
  }
  return {
    enrollment() {
      return createEnrollmentClient({
        authServer: origin,
        token: () => storage.getItem(TOKEN) ?? undefined,
        send,
      })
    },
    async account(): Promise<SharingAccount> {
      const value = (await request('/auth/me')) as Partial<SharingAccount>
      if (
        !value ||
        typeof value.id !== 'string' ||
        typeof value.email !== 'string'
      )
        throw new Error('Invalid account response')
      return {
        id: value.id,
        email: value.email,
        name: typeof value.name === 'string' ? value.name : value.email,
      }
    },
    /** RFC 0345 A4: this person pays for the turn answering their message. */
    pledgePrompt(pledge: { sessionId: string; promptId: string }) {
      return pledgeSessionPrompt(
        origin,
        pledge,
        storage.getItem(TOKEN) ?? '',
        send,
      )
    },
    billingTicket(scope: SessionBillingScope) {
      return issueSessionBillingTicket(
        origin,
        scope,
        storage.getItem(TOKEN) ?? '',
        send,
      )
    },
    backups() {
      return request('/keyhive/recovery')
    },
    saveBackup(envelope: unknown) {
      return request('/keyhive/recovery', envelope)
    },
    /** RFC 0345 3a: the first device records where this account's World is,
     * so a new device knows what to ask for. Identical retries succeed; a
     * different location is refused. Approvers check it; it grants nothing. */
    claimLocation(location: { personId: string; worldId: string }) {
      return request('/keyhive/account', location)
    },
    createInvite(invite: unknown) {
      return request('/keyhive/invites', invite)
    },
    /** 403 when signed in as another address, 410 once expired. */
    invite(id: string) {
      return request(`/keyhive/invites/${id}`)
    },
    /** S16: the person the invited address's account recorded, or null. */
    async personOf(
      id: string,
    ): Promise<{ person: string; events: Uint8Array[] } | null> {
      const answer = (await request(`/keyhive/invites/${id}/person`)) as {
        person?: unknown
        events?: unknown
      }
      if (typeof answer.person !== 'string' || !Array.isArray(answer.events))
        return null
      return {
        person: answer.person,
        events: (answer.events as unknown[]).flatMap((e) =>
          typeof e === 'string'
            ? [Uint8Array.from(atob(e), (c) => c.charCodeAt(0))]
            : [],
        ),
      }
    },
    /** S16: publish this person's signed events so others can invite them
     * directly. Signed: the server holding them forges nothing. */
    publishPersonEvents(events: Uint8Array[]) {
      return request('/keyhive/invites/person-events', {
        events: events.map((e) => btoa(String.fromCharCode(...e))),
      })
    },
    /** S16: this device granted that person, so the invite lists for them. */
    markGranted(id: string) {
      return request(`/keyhive/invites/${id}/granted`, {})
    },
    /** S16: invites to this account that an inviter already granted. */
    async myInvites(): Promise<
      Array<{
        id: string
        title: string
        kind: 'project' | 'session' | 'document'
        inviterName: string
        targetId: string
      }>
    > {
      const answer = (await request('/keyhive/invites/mine')) as {
        invites?: unknown
      }
      return Array.isArray(answer.invites) ? (answer.invites as never) : []
    },
    /** The inviter asks whether a person its invite admitted is the invited
     * address's own person (RFC 0345 review 4). Throws when it cannot tell. */
    async isInvitee(id: string, person: string): Promise<boolean> {
      const answer = (await request(
        `/keyhive/invites/${id}/invitee/${person}`,
      )) as { invitee?: unknown }
      return answer.invitee === true
    },
    signOut() {
      storage.removeItem(TOKEN)
    },
  }
}
